import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ErreurApi, type OperationEnSouffrance, type OperationTerrain } from './api';
import { Icone } from './icones';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TERRAIN — ce qui se saisit sans réseau, et ce qui n'est pas entré (D.930)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Un agent part trois jours en préfecture. Il saisit des bornes et des
 * riverains sans réseau. Au retour, tout doit remonter — et RIEN ne doit
 * disparaître en chemin.
 *
 * ⚠️⚠️ LA RÈGLE QUI TIENT TOUT : ON N'EFFACE QUE CE QUE LE SERVICE DÉCLARE
 * EFFAÇABLE. La réponse de `/terrain/synchroniser` porte un champ
 * `effacables` — la liste des clés entrées au registre ou déjà vues. L'écran
 * ne décide pas lui-même : un appareil qui marque « envoyé » sur une opération
 * refusée perd la saisie définitivement, et l'agent ne le sait qu'au bornage.
 *
 * ⚠️ TROIS ÉTATS DE SORTIE, ET ILS NE VEULENT PAS DIRE LA MÊME CHOSE :
 *   · `appliquee` / `deja_vue` → entrée au registre, la file peut l'oublier ;
 *   · `conflit`   → le registre a bougé depuis la saisie ; la file la GARDE ;
 *   · `refusee`   → le registre l'a refusée avec un motif ; la file la GARDE.
 *
 * ⚠️ CE QUI EST FAIT ET CE QUI NE L'EST PAS, DIT À L'ÉCRAN. La file locale
 * survit au rechargement de la page et à la coupure du réseau. Elle ne survit
 * PAS à la fermeture du navigateur hors ligne : l'application elle-même n'est
 * pas encore mise en cache. Le dire est le minimum ; le taire ferait partir un
 * agent trois jours avec une confiance qu'il n'a pas.
 */

const CLE_FILE = 'mowibo.cadastral.terrain.file.v1';
const CLE_APPAREIL = 'mowibo.cadastral.terrain.appareil.v1';

interface EnFile extends OperationTerrain {
  leveReference: string;
  /** Ce que la dernière tentative a rendu. `null` = jamais envoyée. */
  dernierEtat: string | null;
  dernierMotif: string | null;
}

function lireFile(): EnFile[] {
  try {
    const brut = localStorage.getItem(CLE_FILE);
    return brut ? (JSON.parse(brut) as EnFile[]) : [];
  } catch {
    /* ⚠️ UNE FILE ILLISIBLE NE DOIT PAS VIDER LA FILE. On rend vide pour
       l'affichage, on n'écrit RIEN : écraser ici effacerait une saisie de
       terrain à cause d'un octet abîmé. */
    return [];
  }
}

function ecrireFile(f: EnFile[]) {
  try { localStorage.setItem(CLE_FILE, JSON.stringify(f)); } catch { /* quota : la saisie reste en mémoire */ }
}

function nomAppareil(): string {
  try {
    let n = localStorage.getItem(CLE_APPAREIL);
    if (!n) {
      n = `tablette-${Math.random().toString(36).slice(2, 7)}`;
      localStorage.setItem(CLE_APPAREIL, n);
    }
    return n;
  } catch { return 'appareil-sans-memoire'; }
}

/** UUID v4 — généré hors réseau, c'est lui qui rend le rejeu sans effet. */
function cle(): string {
  const c = globalThis.crypto;
  if (c && 'randomUUID' in c) return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = Math.floor(Math.random() * 16);
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const TYPES_BORNE = ['borne_beton', 'borne_pierre', 'piquet_fer', 'angle_de_mur', 'angle_de_cloture', 'arbre_repere', 'point_peint', 'non_materialisee'];
const ETATS_BORNE = ['posee', 'retrouvee', 'detruite', 'retablie', 'deplacee'];
const VERDICTS = ['concordant', 'opposition', 'absent', 'refus_de_signer'];

export function Terrain() {
  const [file, setFile] = useState<EnFile[]>(lireFile);
  const [souffrance, setSouffrance] = useState<OperationEnSouffrance[]>([]);
  const [enLigne, setEnLigne] = useState(navigator.onLine);
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [rapport, setRapport] = useState<string | null>(null);
  const appareil = useMemo(nomAppareil, []);

  // Saisie
  const [leve, setLeve] = useState('');
  const [objet, setObjet] = useState<'borne' | 'riverain'>('borne');
  const [borne, setBorne] = useState({ numero: '', longitude: '', latitude: '', typeBorne: 'borne_beton', etat: 'posee', precisionM: '0.03' });
  const [riverain, setRiverain] = useState({ nomDeclare: '', cote: 'nord', verdict: 'concordant', motif: '' });

  useEffect(() => {
    const haut = () => setEnLigne(true);
    const bas = () => setEnLigne(false);
    window.addEventListener('online', haut);
    window.addEventListener('offline', bas);
    return () => { window.removeEventListener('online', haut); window.removeEventListener('offline', bas); };
  }, []);

  const rafraichirSouffrance = useCallback(() => {
    if (!navigator.onLine) return;
    api.terrain.enSouffrance().then((r) => setSouffrance(r.operations)).catch(() => { /* hors ligne : on garde l'affichage */ });
  }, []);
  useEffect(rafraichirSouffrance, [rafraichirSouffrance]);

  const ajouter = useCallback(() => {
    if (!leve.trim()) { setErreur('Indiquez le levé auquel cette saisie se rattache.'); return; }
    setErreur(null);
    const charge = objet === 'borne'
      ? { numero: borne.numero, longitude: Number(borne.longitude), latitude: Number(borne.latitude),
          typeBorne: borne.typeBorne, etat: borne.etat, precisionM: Number(borne.precisionM) }
      : { nomDeclare: riverain.nomDeclare, cote: riverain.cote, verdict: riverain.verdict,
          motif: riverain.motif || undefined };
    const op: EnFile = {
      cleAppareil: cle(),
      objetType: objet,
      objetCle: `${leve.trim()}/${objet === 'borne' ? borne.numero : riverain.cote}`,
      verbe: 'creer',
      saisieLe: new Date().toISOString(),
      charge,
      leveReference: leve.trim(),
      dernierEtat: null, dernierMotif: null,
    };
    const suivant = [...file, op];
    setFile(suivant); ecrireFile(suivant);
    if (objet === 'borne') setBorne({ ...borne, numero: '', longitude: '', latitude: '' });
    else setRiverain({ ...riverain, nomDeclare: '', motif: '' });
  }, [leve, objet, borne, riverain, file]);

  const remonter = useCallback(async () => {
    const parLeve = new Map<string, EnFile[]>();
    for (const o of file) {
      if (o.dernierEtat === 'appliquee' || o.dernierEtat === 'deja_vue') continue;
      const L = parLeve.get(o.leveReference) ?? [];
      L.push(o); parLeve.set(o.leveReference, L);
    }
    if (parLeve.size === 0) { setRapport('Rien à remonter.'); return; }

    setOccupe(true); setErreur(null);
    let appliquees = 0, gardees = 0;
    let restant = [...file];
    try {
      for (const [leveReference, ops] of parLeve) {
        const r = await api.terrain.synchroniser({
          appareil, leveReference,
          operations: ops.map(({ leveReference: _l, dernierEtat: _e, dernierMotif: _m, ...op }) => op),
        });
        appliquees += r.appliquees + r.dejaVues;
        /**
         * ⚠️ C'EST ICI QUE TOUT SE JOUE. On n'efface QUE `effacables`, la liste
         * que le SERVICE rend. Effacer sur « pas d'erreur HTTP » perdrait les
         * conflits et les refus, qui sont précisément ce qu'il faut garder.
         */
        const effacables = new Set(r.effacables);
        const etats = new Map(r.detail.map((d) => [d.cleAppareil, d]));
        restant = restant
          .filter((o) => !effacables.has(o.cleAppareil))
          .map((o) => {
            const d = etats.get(o.cleAppareil);
            return d ? { ...o, dernierEtat: d.etat, dernierMotif: d.motif } : o;
          });
        gardees += r.conflits + r.refusees;
      }
      setFile(restant); ecrireFile(restant);
      setRapport(`${appliquees} entrée(s) au registre · ${gardees} gardée(s) dans la file, avec leur motif.`);
      rafraichirSouffrance();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Remontée impossible — la file est intacte.');
    } finally { setOccupe(false); }
  }, [file, appareil, rafraichirSouffrance]);

  const aRemonter = file.filter((o) => o.dernierEtat !== 'appliquee' && o.dernierEtat !== 'deja_vue');

  return (
    <div className="terr-ecran">
      <header className="terr-tete">
        <h2><Icone nom="terrain" taille={20} /> Terrain</h2>
        <p>
          Saisissez bornes et riverains <strong>même sans réseau</strong>. Rien ne part tant
          que vous ne le demandez pas, et rien n’est effacé de cette file tant que le
          registre ne l’a pas accepté.
        </p>
        <div className={`terr-reseau ${enLigne ? 'terr-connecte' : 'terr-coupe'}`}>
          <b>{enLigne ? 'Réseau disponible' : 'Hors réseau'}</b>
          <span>{appareil}</span>
        </div>
      </header>

      {/* ⚠️ CE QUE L'AGENT DOIT SAVOIR AVANT DE PARTIR, ET QU'ON NE TAIT PAS. */}
      <div className="terr-limite">
        <Icone nom="alerte" taille={16} />
        <span>
          La file survit au rechargement de la page et à la coupure du réseau. Elle ne
          survit <strong>pas</strong> à la fermeture du navigateur hors réseau : l’application
          elle-même n’est pas encore mise en cache. Gardez l’onglet ouvert.
        </span>
      </div>

      <section className="terr-bloc">
        <h3>Saisir</h3>
        <div className="terr-formulaire">
          <label>Levé
            <input value={leve} onChange={(e) => setLeve(e.target.value)} placeholder="GN-LEV-000000004" />
          </label>
          <label>Objet
            <select value={objet} onChange={(e) => setObjet(e.target.value as 'borne' | 'riverain')}>
              <option value="borne">Borne</option>
              <option value="riverain">Riverain</option>
            </select>
          </label>
        </div>

        {objet === 'borne' ? (
          <div className="terr-formulaire">
            <label>Numéro<input value={borne.numero} onChange={(e) => setBorne({ ...borne, numero: e.target.value })} placeholder="B1" /></label>
            <label>Longitude<input type="number" step="0.0000001" value={borne.longitude} onChange={(e) => setBorne({ ...borne, longitude: e.target.value })} /></label>
            <label>Latitude<input type="number" step="0.0000001" value={borne.latitude} onChange={(e) => setBorne({ ...borne, latitude: e.target.value })} /></label>
            <label>Type
              <select value={borne.typeBorne} onChange={(e) => setBorne({ ...borne, typeBorne: e.target.value })}>
                {TYPES_BORNE.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </label>
            <label>État
              <select value={borne.etat} onChange={(e) => setBorne({ ...borne, etat: e.target.value })}>
                {ETATS_BORNE.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label>Précision (m)<input type="number" step="0.01" value={borne.precisionM} onChange={(e) => setBorne({ ...borne, precisionM: e.target.value })} /></label>
          </div>
        ) : (
          <div className="terr-formulaire">
            <label>Nom déclaré<input value={riverain.nomDeclare} onChange={(e) => setRiverain({ ...riverain, nomDeclare: e.target.value })} /></label>
            <label>Côté<input value={riverain.cote} onChange={(e) => setRiverain({ ...riverain, cote: e.target.value })} /></label>
            <label>Verdict
              <select value={riverain.verdict} onChange={(e) => setRiverain({ ...riverain, verdict: e.target.value })}>
                {VERDICTS.map((v) => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}
              </select>
            </label>
            <label>Motif<input value={riverain.motif} onChange={(e) => setRiverain({ ...riverain, motif: e.target.value })} /></label>
          </div>
        )}

        <div className="terr-gestes">
          <button className="terr-principal" onClick={ajouter}>Ajouter à la file</button>
          <span className="terr-aide">L’ajout est local : aucun réseau n’est requis.</span>
        </div>
        {erreur && <div className="terr-refus"><Icone nom="alerte" taille={16} /> {erreur}</div>}
      </section>

      <section className="terr-bloc">
        <h3>La file de cet appareil — {file.length} opération(s)</h3>
        {file.length === 0
          ? <p className="terr-aide">Rien en attente.</p>
          : (
            <>
              <div className="terr-defile">
                <table className="terr-table">
                  <thead><tr><th>Objet</th><th>Rattaché à</th><th>Saisi le</th><th>État</th></tr></thead>
                  <tbody>
                    {file.map((o) => (
                      <tr key={o.cleAppareil} className={o.dernierEtat === 'conflit' || o.dernierEtat === 'refusee' ? 'terr-garde' : ''}>
                        <td>{o.objetType}</td>
                        <td>{o.objetCle}</td>
                        <td>{o.saisieLe.slice(0, 16).replace('T', ' ')}</td>
                        <td>
                          {o.dernierEtat === null ? <em>jamais envoyée</em> : <b>{o.dernierEtat}</b>}
                          {o.dernierMotif && <p>{o.dernierMotif}</p>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="terr-gestes">
                <button className="terr-principal" disabled={!enLigne || occupe || aRemonter.length === 0} onClick={() => void remonter()}>
                  {occupe ? 'Remontée…' : `Remonter ${aRemonter.length} opération(s)`}
                </button>
                {!enLigne && <span className="terr-aide">Hors réseau — la remontée attendra.</span>}
              </div>
              {rapport && <p className="terr-rapport"><Icone nom="valide" taille={15} /> {rapport}</p>}
            </>
          )}
      </section>

      <section className="terr-bloc">
        <h3>Ce qui n’est PAS entré au registre — tous appareils</h3>
        <p className="terr-aide">
          Une file qui n’affiche que ses succès perd des données en silence : personne
          ne va chercher ce qui manque. Ces opérations sont remontées, tracées, et
          n’ont rien écrit.
        </p>
        {souffrance.length === 0
          ? <p className="terr-aide">Aucune opération en souffrance.</p>
          : (
            <div className="terr-defile">
              <table className="terr-table">
                <thead><tr><th>Appareil</th><th>Objet</th><th>État</th><th>Pourquoi</th><th>Reçue le</th></tr></thead>
                <tbody>
                  {souffrance.map((o, i) => (
                    <tr key={i} className="terr-garde">
                      <td>{o.appareil}</td>
                      <td>{o.objet_type} · {o.objet_cle}</td>
                      <td><b>{o.etat}</b></td>
                      <td>{o.motif ?? <em>—</em>}</td>
                      <td>{o.recue_le?.slice(0, 16).replace('T', ' ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </section>
    </div>
  );
}
